import React, { useState, useEffect, useCallback, useMemo } from 'react';
// [Web UX 3. csomag (D), 2026-10-04] A kérés-hibák szövege a közös `apiErrorMessage`-en át megy (a szerver magyar
// indoklása / hálózati hibánál magyar mondat / különben „<tartalék> (HTTP n)") — a korábbi FIX
// szövegű ágak eldobták a szerver indoklását. Őr: `utils/__tests__/rawErrorMessages.guard.test.ts`.
import { apiErrorMessage } from '../utils/apiErrors';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  IconButton,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  CheckCircle as PaidIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import attendanceService, {
  UserPrivateLessonDebt,
  PrivateLessonReportRow,
} from '../services/attendanceService';
import { useFeedback } from '../contexts/FeedbackContext';
import { formatHours } from '../utils/coachPresence';

const formatDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-');
  return `${year}. ${month}. ${day}.`;
};

const formatTime = (timeStr: string) => timeStr.slice(0, 5);

const sessionHours = (startTime: string, endTime: string): number => {
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  return ((eh * 60 + em) - (sh * 60 + sm)) / 60;
};

// A közös, magyar alakú óraszám-formázó (`1,5 óra`) — a korábbi helyi változat a nyers
// számot írta ki, így a 90 perces magánóra „1.5 óra"-ként jelent meg (angol pont).
const formatHoursLabel = (h: number): string => `${formatHours(h)} óra`;

const formatFt = (n?: number | null): string =>
  n == null ? '—' : `${new Intl.NumberFormat('hu-HU').format(n)} Ft`;

interface RowProps {
  debt: UserPrivateLessonDebt;
  onMarkPaid: (attendanceId: string) => void;
  markingIds: Set<string>;
}

const DebtRow: React.FC<RowProps> = ({ debt, onMarkPaid, markingIds }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{debt.userName}</TableCell>
        <TableCell>{debt.userEmail}</TableCell>
        <TableCell>{debt.privateLessonFee != null ? formatFt(debt.privateLessonFee) : <Typography variant="body2" color="text.secondary">nincs beállítva</Typography>}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={`${debt.unpaidCount} alkalom`} color="error" size="small" />
            {debt.amountDue != null && (
              <Chip label={formatFt(debt.amountDue)} color="error" size="small" variant="outlined" />
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dátum</TableCell>
                    <TableCell>Időpont</TableCell>
                    <TableCell>Időtartam</TableCell>
                    <TableCell>Edző</TableCell>
                    <TableCell align="right">Művelet</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debt.sessions.map((session) => (
                    <TableRow key={session.attendanceId}>
                      <TableCell>{formatDate(session.eventDate)}</TableCell>
                      <TableCell>{formatTime(session.startTime)} – {formatTime(session.endTime)}</TableCell>
                      <TableCell>{formatHoursLabel(sessionHours(session.startTime, session.endTime))}</TableCell>
                      <TableCell>{session.coachName}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Rendezettnek jelöl">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={
                                markingIds.has(session.attendanceId) ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <PaidIcon />
                                )
                              }
                              disabled={markingIds.has(session.attendanceId)}
                              onClick={() => onMarkPaid(session.attendanceId)}
                            >
                              Rendezve
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const PrivateLessonDebts: React.FC = () => {
  const { showSuccess } = useFeedback();
  const [mode, setMode] = useState<'debts' | 'report'>('debts');

  const [debts, setDebts] = useState<UserPrivateLessonDebt[]>([]);
  const [report, setReport] = useState<PrivateLessonReportRow[]>([]);
  const [reportLoaded, setReportLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await attendanceService.getPrivateLessonDebts();
      setDebts(data);
    } catch (err) {
      setError(apiErrorMessage(err, 'Nem sikerült betölteni az adatokat.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await attendanceService.getPrivateLessonReport();
      setReport(data);
      setReportLoaded(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Nem sikerült betölteni a riportot.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (mode === 'report' && !reportLoaded) {
      loadReport();
    }
  }, [mode, reportLoaded, loadReport]);

  const handleMarkPaid = async (attendanceId: string) => {
    setMarkingIds((prev) => new Set(prev).add(attendanceId));
    try {
      await attendanceService.markAsPaid(attendanceId);
      showSuccess('Az alkalom rendezettnek jelölve.');
      setDebts((prev) =>
        prev
          .map((d) => {
            const sessions = d.sessions.filter((s) => s.attendanceId !== attendanceId);
            const unpaidCount = sessions.length;
            const amountDue = d.privateLessonFee != null ? unpaidCount * d.privateLessonFee : d.amountDue;
            return { ...d, sessions, unpaidCount, amountDue };
          })
          .filter((d) => d.unpaidCount > 0)
      );
      // The report (if already loaded) is now stale — force a refresh next time it's opened.
      setReportLoaded(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Nem sikerült a rendezettnek jelölés.'));
    } finally {
      setMarkingIds((prev) => {
        const next = new Set(prev);
        next.delete(attendanceId);
        return next;
      });
    }
  };

  const filteredDebts = useMemo(() => {
    if (!search.trim()) return debts;
    const lower = search.toLowerCase();
    return debts.filter(
      (d) => d.userName.toLowerCase().includes(lower) || d.userEmail.toLowerCase().includes(lower)
    );
  }, [debts, search]);

  const filteredReport = useMemo(() => {
    if (!search.trim()) return report;
    const lower = search.toLowerCase();
    return report.filter(
      (r) => r.userName.toLowerCase().includes(lower) || r.userEmail.toLowerCase().includes(lower)
    );
  }, [report, search]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Magánóra – tartozások és díjak
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
        >
          <ToggleButton value="debts">Tartozások</ToggleButton>
          <ToggleButton value="report">Összes magánórázó</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder="Szűrés névre vagy emailre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 320 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : mode === 'debts' ? (
        debts.length === 0 ? (
          <Alert severity="success">Nincs kifizetetlen magánóra.</Alert>
        ) : filteredDebts.length === 0 ? (
          <Alert severity="info">Nincs a szűrésnek megfelelő találat.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={50} />
                  <TableCell>Játékos neve</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Magánóra-díj</TableCell>
                  <TableCell>Tartozás</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredDebts.map((debt) => (
                  <DebtRow
                    key={debt.userId}
                    debt={debt}
                    onMarkPaid={handleMarkPaid}
                    markingIds={markingIds}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : report.length === 0 ? (
        <Alert severity="info">Még nem volt magánóra-részvétel.</Alert>
      ) : filteredReport.length === 0 ? (
        <Alert severity="info">Nincs a szűrésnek megfelelő találat.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Játékos neve</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Magánóra-díj</TableCell>
                <TableCell align="right">Alkalmak</TableCell>
                <TableCell align="right">Fizetett</TableCell>
                <TableCell align="right">Kifizetetlen</TableCell>
                <TableCell align="right">Beszedve</TableCell>
                <TableCell align="right">Kinnlévő</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReport.map((r) => (
                <TableRow key={r.userId} hover>
                  <TableCell>{r.userName}</TableCell>
                  <TableCell>{r.userEmail}</TableCell>
                  <TableCell align="right">{formatFt(r.privateLessonFee)}</TableCell>
                  <TableCell align="right">{r.totalLessons}</TableCell>
                  <TableCell align="right">{r.paidCount}</TableCell>
                  <TableCell align="right">
                    {r.unpaidCount > 0 ? <Chip label={r.unpaidCount} color="error" size="small" /> : r.unpaidCount}
                  </TableCell>
                  <TableCell align="right">{formatFt(r.totalCollected)}</TableCell>
                  <TableCell align="right">{formatFt(r.outstandingAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1.5 }}>
            „Beszedve" = a ténylegesen befizetett összeg. „Kinnlévő" = kifizetetlen alkalmak × a jelenleg
            beállított magánóra-díj. A kettő külön alapon számol, ezért nem összeadandó.
          </Typography>
        </TableContainer>
      )}
    </Box>
  );
};

export default PrivateLessonDebts;
